import React, { useState, useEffect } from "react";
import { Link } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLawfirmId } from "../../../../../store/slices/lawFirmSlice";
import { toast } from "react-toastify";
import { apiClient } from "../../../../../api/httpClient";

function getBondReassignSuccessMessage(updatedCount) {
  if (!updatedCount) return "Re-assignment completed.";
  const bondWord = updatedCount > 1 ? "bonds" : "bond";
  return `${updatedCount} ${bondWord} re-assigned successfully.`;
}

function useLawFirmDetails() {
  const navigate = useNavigate();
  const { id } = useParams(); // Assuming lawfirmId is passed in the route
  const dispatch = useDispatch();
  const [lawFirmData, setLawFirmData] = useState({});
  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: [
      {
        label: "Bond History",
        backgroundColor: "#1976d2", // Blue theme for chart
        data: [],
      },
    ],
  });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Manages saving loader
  const [teamMembers, setTeamMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const { role } = useSelector((state) => state.auth);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Search states
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Pagination state for locations
  const [locationsPage, setLocationsPage] = useState(0);
  const [locationsRowsPerPage, setLocationsRowsPerPage] = useState(5);

  // Pagination state for team members
  const [teamMembersPage, setTeamMembersPage] = useState(0);
  const [teamMembersRowsPerPage, setTeamMembersRowsPerPage] = useState(5);

  // --- Location Delete State ---
  const [deleteLocationDialogOpen, setDeleteLocationDialogOpen] =
    useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [deleteLocationLoading, setDeleteLocationLoading] = useState(false);

  // --- Law Firm User Delete State ---
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // --- Internal Users for Managed by ---
  const [internalUsers, setInternalUsers] = useState([]);

  // --- Re-assign Policies Dialog State ---
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignFromMember, setReassignFromMember] = useState(null);
  const [replacementUsers, setReplacementUsers] = useState([]);
  const [selectedReplacementUser, setSelectedReplacementUser] = useState(null);
  const [selectedBondOptions, setSelectedBondOptions] = useState([]);
  const [availableBonds, setAvailableBonds] = useState([]);
  const [bondsLoading, setBondsLoading] = useState(false);
  const [bondsError, setBondsError] = useState("");
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  // --- Law Firm Re-assignment Logs Dialog State ---
  const [reassignLogsDialogOpen, setReassignLogsDialogOpen] = useState(false);
  const [reassignLogs, setReassignLogs] = useState([]);
  const [reassignLogsLoading, setReassignLogsLoading] = useState(false);
  const [reassignLogsError, setReassignLogsError] = useState("");

  const fetchInternalUsers = async () => {
    try {
      const internalUsers = await apiClient("/internal-user", "GET");
      setInternalUsers(internalUsers.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLawFirmDetails = async () => {
    if (!id) {
      console.warn("id is undefined or null");
      return;
    }
    try {
      localStorage.setItem("lawfirm_id", id);
      setLoading(true);
      const lawFirmData = await apiClient(`/lawfirm/${id}`, "GET");
      setLawFirmData(lawFirmData);

      // Fetch locations
      const locationData = await apiClient(
        `/get-locations-by-lawfirm/${id}`,
        "GET"
      );
      setLocations(locationData);

      // Fetch team members
      const teamData = await apiClient(`/lawfirm-user-location/${id}`, "GET");
      setTeamMembers(teamData);
    } catch (error) {
      console.error("Error fetching law firm details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBondHistoryData = async () => {
    try {
      const response = await apiClient(
        `/count-by-year/?law_firm_id=${id}`,
        "GET"
      );
      const data = response;
      const labels = data.map((item) => item.year || "Unknown");
      const counts = data.map((item) => item.count);
      setGraphData({
        labels,
        datasets: [
          {
            label: "Bond History",
            backgroundColor: "#1976d2",
            data: counts,
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching graph data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch law firm details, locations, and team members on mount
  useEffect(() => {
    fetchLawFirmDetails();
    fetchBondHistoryData();
    fetchInternalUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => setIsEditing(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await apiClient(`/lawfirm/${id}`, "PATCH", lawFirmData);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating law firm details:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (index, field, setFunction, data) => {
    const updatedData = [...data];
    updatedData[index][field] = !updatedData[index][field];
    setFunction(updatedData);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset search queries when switching tabs
    setLocationSearchQuery("");
    setUserSearchQuery("");
    // Reset pagination when switching tabs
    setLocationsPage(0);
    setTeamMembersPage(0);
  };

  const handleViewLawFirmBonds = () => {
    if (!id) return;
    const numericId = Number(id);
    dispatch(setLawfirmId(Number.isNaN(numericId) ? id : numericId));
    navigate("/vermost_approve_bonds_lawfirm");
  };

  // Search handlers
  const handleLocationSearch = (event) => {
    setLocationSearchQuery(event.target.value);
    setLocationsPage(0); // Reset to first page when searching
  };

  const handleUserSearch = (event) => {
    setUserSearchQuery(event.target.value);
    setTeamMembersPage(0); // Reset to first page when searching
  };

  const clearLocationSearch = () => {
    setLocationSearchQuery("");
    setLocationsPage(0);
  };

  const clearUserSearch = () => {
    setUserSearchQuery("");
    setTeamMembersPage(0);
  };

  // Pagination handlers for locations
  const handleLocationsPageChange = (event, newPage) => {
    setLocationsPage(newPage);
  };

  const handleLocationsRowsPerPageChange = (event) => {
    setLocationsRowsPerPage(Number.parseInt(event.target.value, 10));
    setLocationsPage(0);
  };

  // Pagination handlers for team members
  const handleTeamMembersPageChange = (event, newPage) => {
    setTeamMembersPage(newPage);
  };

  const handleTeamMembersRowsPerPageChange = (event) => {
    setTeamMembersRowsPerPage(Number.parseInt(event.target.value, 10));
    setTeamMembersPage(0);
  };

  // Filter functions
  const filterLocations = (locations, searchQuery) => {
    if (!searchQuery.trim()) return locations;
    
    const query = searchQuery.toLowerCase().trim();
    return locations.filter(location => 
      location.name?.toLowerCase().includes(query)
    );
  };

  const filterTeamMembers = (members, searchQuery) => {
    if (!searchQuery.trim()) return members;
    
    const query = searchQuery.toLowerCase().trim();
    return members.filter(member => 
      member.lawfirm_user_name?.toLowerCase().includes(query)
    );
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBondRetrievePath = (bondTypeRaw, bondId) => {
    if (!bondTypeRaw || !bondId) return null;
    const key = String(bondTypeRaw).toLowerCase().replace(/[^a-z]/g, "");

    switch (key) {
      case "probate":
        return `/vermost-probate-bond-update/${bondId}`;
      case "guardianship":
        return `/vermost-guardianship-bond-update/${bondId}`;
      case "trustee":
        return `/vermost-trustee-bond-update/${bondId}`;
      case "va":
        return `/vermost-va-bond-update/${bondId}`;
      case "blanket":
        return `/vermost-blanket-bond-update/${bondId}`;
      default:
        return null;
    }
  };

  const renderReassignComment = (comment) => {
    const text = comment == null ? "" : String(comment);
    if (!text.trim()) return <>-</>;

    // Matches "(Probate-6644)" / "(VA-123)" etc. and turns those into links.
    const regex = /\(([A-Za-z ]+)-(\d+)\)/g;
    let match;
    let lastIndex = 0;
    const nodes = [];

    while ((match = regex.exec(text)) !== null) {
      const [full, bondType, bondId] = match;
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }

      const href = getBondRetrievePath(bondType, bondId);
      if (href) {
        nodes.push(
          <Link
            key={`${match.index}-${bondType}-${bondId}`}
            href={href}
            target="_blank"
            rel="noreferrer"
            underline="always"
            sx={{ fontWeight: 600 }}
          >
            {full}
          </Link>
        );
      } else {
        nodes.push(full);
      }

      lastIndex = match.index + full.length;
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes.length ? <>{nodes}</> : <>{text}</>;
  };

  const formatDateForInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  };

  // Get filtered and paginated data
  const filteredLocations = filterLocations(locations, locationSearchQuery);
  const filteredTeamMembers = filterTeamMembers(teamMembers, userSearchQuery);

  const paginatedLocations = filteredLocations.slice(
    locationsPage * locationsRowsPerPage,
    locationsPage * locationsRowsPerPage + locationsRowsPerPage
  );

  const paginatedTeamMembers = filteredTeamMembers.slice(
    teamMembersPage * teamMembersRowsPerPage,
    teamMembersPage * teamMembersRowsPerPage + teamMembersRowsPerPage
  );

  // --- Location Delete Logic ---
  const handleDeleteLocationClick = (location) => {
    setSelectedLocation(location);
    setDeleteLocationDialogOpen(true);
  };

  const handleConfirmDeleteLocation = async () => {
    if (!selectedLocation) return;
    setDeleteLocationLoading(true);
    try {
      await apiClient(
        `/lawfirm-location/${selectedLocation.law_firm_location_id}`,
        "DELETE"
      );
      setLocations(
        locations.filter(
          (loc) =>
            loc.law_firm_location_id !== selectedLocation.law_firm_location_id
        )
      );
    } catch (error) {
      console.error("Error deleting location:", error);
    } finally {
      setDeleteLocationLoading(false);
      setDeleteLocationDialogOpen(false);
      setSelectedLocation(null);
    }
  };

  // --- Law Firm User Delete Logic ---
  const handleDeleteUserClick = (user) => {
    setSelectedUser(user);
    setDeleteUserDialogOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!selectedUser) return;
    setDeleteUserLoading(true);
    try {
      await apiClient(
        `/lawfirm-user/${selectedUser.lawfirm_user_id}/${selectedUser.location_id}`,
        "DELETE"
      );
      setTeamMembers(
        teamMembers.filter(
          (user) => user.lawfirm_user_id !== selectedUser.lawfirm_user_id
        )
      );
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setDeleteUserLoading(false);
      setDeleteUserDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const openReassignDialog = async (member) => {
    setReassignFromMember(member);
    const samePositionUsers =
      teamMembers?.filter(
        (u) =>
          u.Position === member.Position &&
          u.lawfirm_user_id !== member.lawfirm_user_id
      ) || [];
    setReplacementUsers(samePositionUsers);
    setSelectedReplacementUser(null);
    setSelectedBondOptions([]);
    setAvailableBonds([]);
    setBondsError("");
    setReassignDialogOpen(true);

    if (!member?.lawfirm_user_id) return;
    try {
      setBondsLoading(true);
      const data = await apiClient(
        `/list-lawfirm-user-bonds/${member.lawfirm_user_id}/`,
        "GET"
      );
      setAvailableBonds(Array.isArray(data) ? data : []);
      if (!data?.length) {
        setBondsError("No bonds found for this user.");
      }
    } catch (error) {
      console.error("Failed to load bonds for law firm user", error);
      setAvailableBonds([]);
      setBondsError("Failed to load bonds for this user.");
    } finally {
      setBondsLoading(false);
    }
  };

  const handleSubmitReassign = async () => {
    if (!selectedReplacementUser || selectedBondOptions.length === 0) return;
    const targetUserId = selectedReplacementUser.value;
    const sourceUserId = reassignFromMember?.lawfirm_user_id;
    const allSelected = selectedBondOptions.some((opt) => opt.value === "ALL");
    const bondIds = allSelected
      ? availableBonds.map((b) => b.bond_id)
      : selectedBondOptions.map((opt) => opt.value);

    if (!bondIds.length) {
      setBondsError("No bonds selected to re-assign.");
      return;
    }

    if (!sourceUserId) {
      setBondsError(
        "Unable to determine the current law firm user for reassignment."
      );
      return;
    }

    try {
      setReassignSubmitting(true);
      const response = await apiClient(
        `/update-lawfirm-user-bonds/${sourceUserId}/`,
        "POST",
        {
          bond_ids: bondIds,
          old_lawfirm_user_id: sourceUserId,
          new_lawfirm_user_id: targetUserId,
        }
      );

      const updatedCount = Number(response?.updated_count || 0);
      toast.success(getBondReassignSuccessMessage(updatedCount));
      setReassignDialogOpen(false);
      setReassignFromMember(null);
      setAvailableBonds([]);
      setSelectedBondOptions([]);
      setSelectedReplacementUser(null);
      setBondsError("");
    } catch (error) {
      console.error("Failed to re-assign bonds", error);
      const errorMessage =
        error?.error ||
        error?.detail ||
        error?.message ||
        "Failed to re-assign bonds. Please check the selection and try again.";
      setBondsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleOpenReassignLogs = async () => {
    if (!id) return;
    setReassignLogsDialogOpen(true);
    setReassignLogs([]);
    setReassignLogsError("");
    setReassignLogsLoading(true);

    try {
      const response = await apiClient(
        `/lawfirm-reassignment-logs/${id}/`,
        "GET"
      );

      const logs = Array.isArray(response?.logs) ? response.logs : [];
      setReassignLogs(logs);

      if (!logs.length) {
        setReassignLogsError(
          "No reassignment activity found for this law firm."
        );
      }
    } catch (error) {
      console.error("Failed to load reassignment logs", error);
      const message =
        error?.detail ||
        error?.message ||
        "Failed to load reassignment logs. Please try again.";
      setReassignLogsError(message);
    } finally {
      setReassignLogsLoading(false);
    }
  };

  return {
    id,
    navigate,
    role,
    lawFirmData,
    setLawFirmData,
    graphData,
    loading,
    isEditing,
    isSaving,
    teamMembers,
    setTeamMembers,
    locations,
    internalUsers,
    tabValue,
    locationSearchQuery,
    userSearchQuery,
    locationsPage,
    locationsRowsPerPage,
    teamMembersPage,
    teamMembersRowsPerPage,
    deleteLocationDialogOpen,
    setDeleteLocationDialogOpen,
    selectedLocation,
    deleteLocationLoading,
    deleteUserDialogOpen,
    setDeleteUserDialogOpen,
    selectedUser,
    deleteUserLoading,
    reassignDialogOpen,
    setReassignDialogOpen,
    reassignFromMember,
    replacementUsers,
    selectedReplacementUser,
    setSelectedReplacementUser,
    selectedBondOptions,
    setSelectedBondOptions,
    availableBonds,
    bondsLoading,
    bondsError,
    reassignSubmitting,
    reassignLogsDialogOpen,
    setReassignLogsDialogOpen,
    reassignLogs,
    reassignLogsLoading,
    reassignLogsError,
    handleEditClick,
    handleCancelClick,
    handleSave,
    handleToggle,
    handleTabChange,
    handleViewLawFirmBonds,
    handleLocationSearch,
    handleUserSearch,
    clearLocationSearch,
    clearUserSearch,
    handleLocationsPageChange,
    handleLocationsRowsPerPageChange,
    handleTeamMembersPageChange,
    handleTeamMembersRowsPerPageChange,
    filteredLocations,
    filteredTeamMembers,
    paginatedLocations,
    paginatedTeamMembers,
    handleDeleteLocationClick,
    handleConfirmDeleteLocation,
    handleDeleteUserClick,
    handleConfirmDeleteUser,
    openReassignDialog,
    handleSubmitReassign,
    handleOpenReassignLogs,
    formatDateForInput,
    formatDateTime,
    renderReassignComment,
  };
}

export default useLawFirmDetails;
